import type { Assistant } from "@inrev/assistant";
import type { AssistantConversationId } from "@inrev/common";
import { Option } from "@inrev/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { ApiError } from "../../../utils/error";
import { useRequest } from "../../../utils/request";
import { boundedPoll } from "../../bounded-poll";
import { assistantConversationQueryKey, assistantConversationsQueryKey } from "../assistant.keys";
import {
	needsGeneratedTitle,
	TITLE_POLL_INTERVAL_MS,
	TITLE_POLL_MAX_MS,
} from "./needsGeneratedTitle";

export type UseFetchAssistantConversationOptions = {
	/**
	 * Poll until the generated title lands. No stream event carries it, so the refetch after a
	 * turn usually comes back without one. Keep it off while a turn streams.
	 */
	pollForTitle?: boolean;
};

/** `null` once the conversation no longer exists, e.g. after empty conversations are cleaned up. */
export const useFetchAssistantConversation = (
	conversationId: AssistantConversationId | undefined,
	{ pollForTitle = false }: UseFetchAssistantConversationOptions = {},
) => {
	const { get } = useRequest();
	const queryClient = useQueryClient();
	const pollStartedAtMsRef = useRef<number | null>(null);

	useEffect(() => {
		pollStartedAtMsRef.current = null;
	}, [conversationId]);

	const query = useQuery({
		queryKey: assistantConversationQueryKey(conversationId),
		meta: { suppressErrorModal: true },
		queryFn: async () => {
			const cached = queryClient.getQueryData<Assistant.Dtos.Conversation.Get.Response | null>(
				assistantConversationQueryKey(conversationId),
			);
			try {
				const fetched = await get<Assistant.Dtos.Conversation.Get.Response>(
					`/v2/admin/assistant/conversations/${conversationId}`,
				);
				// History doesn't poll, so a newly generated title reaches its row through here.
				if (Option.isSome(cached) && Option.isNone(cached.title) && Option.isSome(fetched.title)) {
					void queryClient.invalidateQueries({ queryKey: assistantConversationsQueryKey() });
				}
				return fetched;
			} catch (error) {
				if (error instanceof ApiError && error.statusCode === 404) {
					return null;
				}
				throw error;
			}
		},
		enabled: conversationId !== undefined,
		refetchInterval: (query) => {
			if (!pollForTitle) return false;

			return boundedPoll({
				data: query.state.data,
				shouldPoll: needsGeneratedTitle,
				startedAtMsRef: pollStartedAtMsRef,
				intervalMs: TITLE_POLL_INTERVAL_MS,
				maxMs: TITLE_POLL_MAX_MS,
			});
		},
	});

	return {
		conversation: query.data,
		conversationIsLoading: query.isLoading,
		conversationError: query.error,
	};
};
